import { z } from 'zod';
import {
  descriptionSchema,
  documentIdSchema,
  finiteNumber,
  nameSchema,
  vec3Schema,
} from './common';

/** 環境の物理設定。MVP で物理計算に使うのは `gravity` と `airDensity`（`wind` は P3）。 */
export const environmentPhysicsSchema = z.object({
  /** 重力加速度の大きさ [m/s²]（FR-ENV-005） */
  gravity: finiteNumber.min(0.1).max(30),
  /** 空気密度 [kg/m³]（0 は真空） */
  airDensity: finiteNumber.min(0).max(3),
  /** 風速ベクトル [m/s]（P3） */
  wind: vec3Schema.default([0, 0, 0]),
});

/** 色（`#rrggbb`）。 */
const hexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, {
  message: '#rrggbb の形式で指定してください',
});

/** 地面の材質（見た目）。`grid` は、格子の線を引いた、物理の確認用の地面。 */
export const TERRAIN_MATERIALS = ['grid', 'grass', 'sand', 'snow', 'asphalt', 'sea'] as const;

/** 環境（地形・空・光・天候・物理設定）。 */
export const environmentSchema = z.object({
  id: documentIdSchema,
  name: nameSchema,
  description: descriptionSchema.default(''),
  terrain: z.object({
    type: z.literal('flat'),
    /** 一辺の大きさ [m] */
    size: finiteNumber.min(10).max(10_000),
    material: z.enum(TERRAIN_MATERIALS),
    /** 地面の色。省略は、材質の既定の色（1.2.0 で追加） */
    color: hexColorSchema.optional(),
  }),
  sky: z.object({
    type: z.literal('gradient'),
    /** 空の上の色（1.2.0 で追加。省略は昼の空） */
    top: hexColorSchema.default('#5b9ee1'),
    /** 地平線のあたりの色（1.2.0 で追加。省略は昼の空） */
    horizon: hexColorSchema.default('#bfd9f2'),
  }),
  /** 霧（1.2.0 で追加。0 = なし、1 = 濃い。色は空の地平線の色） */
  fog: z.object({ density: finiteNumber.min(0).max(1).default(0) }).default({ density: 0 }),
  lighting: z.object({
    /** 時刻（0〜24 時） */
    timeOfDay: finiteNumber.min(0).max(24),
    /** 太陽の強さ（1 = 標準） */
    sunIntensity: finiteNumber.min(0).max(5),
    /** 太陽の方角 [度]（0 = 北、90 = 東、180 = 南）。省略は、時刻から決める（1.3.0 で追加） */
    sunAzimuth: finiteNumber.min(0).max(360).optional(),
    /** 太陽の高さ [度]（0 = 地平線、90 = 真上）。省略は、時刻から決める（1.3.0 で追加） */
    sunElevation: finiteNumber.min(0).max(90).optional(),
  }),
  weather: z.object({ type: z.literal('clear') }),
  physics: environmentPhysicsSchema,
  /** 環境オブジェクト（木・建物など。P3）。型は P3 で定義する */
  objects: z
    .array(z.looseObject({ type: z.string().min(1).max(64) }))
    .max(1000)
    .default([]),
});

/** 環境。 */
export type Environment = z.infer<typeof environmentSchema>;

const testSpace: Environment = environmentSchema.parse({
  id: 'builtin:environment/test-space',
  name: 'テスト空間',
  description: '平らな地面・空・平行光源だけの、物理の確認用の環境。',
  terrain: { type: 'flat', size: 1000, material: 'grid' },
  sky: { type: 'gradient' },
  lighting: { timeOfDay: 12, sunIntensity: 1 },
  weather: { type: 'clear' },
  physics: { gravity: 9.80665, airDensity: 1.225, wind: [0, 0, 0] },
  objects: [],
});

const moon: Environment = environmentSchema.parse({
  id: 'builtin:environment/moon',
  name: '月面',
  description: '重力が地球の約 1/6、空気がない環境。',
  terrain: { type: 'flat', size: 1000, material: 'grid' },
  sky: { type: 'gradient' },
  lighting: { timeOfDay: 12, sunIntensity: 1.5 },
  weather: { type: 'clear' },
  physics: { gravity: 1.62, airDensity: 0, wind: [0, 0, 0] },
  objects: [],
});

/** 組み込みの環境（FR-ENV-001）。ID は `builtin:environment/...`。 */
export const BUILTIN_ENVIRONMENTS: readonly Environment[] = [testSpace, moon];

/** 既定の環境（テスト空間）の ID。 */
export const DEFAULT_ENVIRONMENT_ID = testSpace.id;
